<template>
  <BaseInputSelect
    ref="selectEl"
    :value="value"
    :options="options"
    :placeholder="placeholder"
    :disabled="disabled"
    :clearable="clearable"
    select-only
    searchable
    size="form"
    @update:value="(v: string) => $emit('update:value', v)"
  >
    <template v-if="showOpenButton" #item="{ option }">
      <BaseInputSelectOptionItem class="automation-item" :title="option.label">
        <template #right>
          <BaseButton
            class="open-button"
            kind="auxiliary"
            @click.stop="openAutomation(option.value, $event)"
          >
            <template #icon>
              <ExternalArrowIcon />
            </template>
          </BaseButton>
        </template>
      </BaseInputSelectOptionItem>
    </template>
  </BaseInputSelect>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";

import type { OrgId } from "@ctrl/controller-rpc";

import { isAssistant } from "@/components/ai/agents";
import type { Automation } from "@/components/ai/useAutomations";
import { ORPHAN_AGENT_KEY } from "@/components/ai/useAutomations";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import ExternalArrowIcon from "@/components/icons/ExternalArrowIcon.vue";
import * as inject from "@/components/inject";
import { navigateToAutomation } from "@/utils/navigate";

/**
 * Searchable dropdown to pick a single automation, fed by the app-wide
 * automations state (`inject.automations()`).
 *
 * The listed automations are the union of:
 * - every agent's automations in each of `organizationIds` (with orphan
 *   automations unless `includeOrphans` is disabled);
 * - the personal assistant's automations when `includeAssistant` is set
 *   (they live in the personal organization, which does not need to be part
 *   of `organizationIds`).
 */
export default defineComponent({
  name: "AutomationPicker",
  components: {
    BaseButton,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    ExternalArrowIcon,
  },
  props: {
    /** Selected automation id; empty string when nothing is selected. */
    value: { type: String, default: "" },
    /** Organizations whose agents' automations are listed. */
    organizationIds: {
      type: Array as PropType<OrgId[]>,
      default: () => [],
    },
    /** Also list the personal assistant's automations. */
    includeAssistant: { type: Boolean, default: false },
    /** List automations of `organizationIds` not owned by any agent. */
    includeOrphans: { type: Boolean, default: true },
    placeholder: { type: String, default: "No automation" },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    /**
     * Show an "open" arrow on each listed automation (on hover) that
     * navigates to the automation's page instead of selecting it.
     */
    showOpenButton: { type: Boolean, default: false },
  },
  emits: {
    "update:value": (_value: string) => true,
  },
  setup(props) {
    const automations = inject.automations();
    const router = inject.router();

    const selectEl = ref<InstanceType<typeof BaseInputSelect>>();

    const pickedAutomations = computed<Automation[]>(() => {
      const state = automations.value;
      if (!state) return [];
      const all = state.all.value ?? {};

      const seen = new Set<string>();
      const list: Automation[] = [];
      const push = (automation: Automation) => {
        if (seen.has(automation.id)) return;
        seen.add(automation.id);
        list.push(automation);
      };

      for (const organizationId of props.organizationIds) {
        const byAgent = all[organizationId];
        if (!byAgent) continue;
        for (const [agentKey, byAutomation] of Object.entries(byAgent)) {
          if (agentKey === ORPHAN_AGENT_KEY && !props.includeOrphans) continue;
          // Assistant automations are indexed under the personal
          // organization too; they are only listed through the dedicated
          // `includeAssistant` opt-in below.
          if (agentKey !== ORPHAN_AGENT_KEY && isAssistant(agentKey)) continue;
          for (const automation of Object.values(byAutomation)) {
            push(automation);
          }
        }
      }

      if (props.includeAssistant) {
        const assistant = state.assistant.value;
        const byAutomation = assistant
          ? (all[assistant.organizationId]?.[assistant.agentId] ?? {})
          : {};
        for (const automation of Object.values(byAutomation)) {
          push(automation);
        }
      }

      return list.sort((a, b) => a.name.localeCompare(b.name));
    });

    const options = computed<SelectOption[]>(() =>
      pickedAutomations.value.map((automation) => ({
        value: automation.id,
        label: automation.name,
        aliases: [automation.name],
      }))
    );

    const openAutomation = async (automationId: string, event?: MouseEvent) => {
      const automation = pickedAutomations.value.find(
        (a) => a.id === automationId
      );
      selectEl.value?.close();
      await navigateToAutomation(
        router,
        automationId,
        automation?.organizationId,
        event
      );
    };

    return { openAutomation, options, selectEl };
  },
});
</script>

<style scoped lang="scss">
.open-button {
  opacity: 0;
  @include transition(opacity 0.15s ease);
}

.automation-item:hover .open-button {
  opacity: 1;
}

// High-specificity selector to outrank both BaseButton's own
// `.base-button.auxiliary.icon-only` padding and the unscoped
// `.completions--form .completion-item svg` sizing (no `!important`).
button.base-button.auxiliary.icon-only.open-button {
  padding: 4px 6px;

  &:hover {
    // Clearly darker than the hovered row's `$grey-05` so the button's own
    // hover reads on top of it.
    @include themed() {
      background: t($grey-20);
    }

    :deep(.fill) {
      @include themed() {
        fill: t($grey-80);
      }
    }
  }

  :deep(svg) {
    width: 12px;
    height: 12px;
  }
}
</style>
